import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckIcon, CircleDashedIcon } from 'lucide-react';
import { FilterChip } from '../components/FilterChip';
import { MetricRow } from '../components/MetricRow';
import { PageHeader } from '../components/PageHeader';
import { SportEvolutionChart } from '../components/SportEvolutionChart';
import { Card, SectionTitle } from '../components/ui/Card';
import { strengthMilestones } from '../data/insights';
import { SportProfileFields } from '../components/SportProfileFields';
import { SportMetricGroupRow } from '../components/SportMetricGroupRow';
import { findSportMetric, metricGroupsForSport, sports, standaloneMetrics } from '../data/sportMetrics';
import { useLocale } from '../contexts/LocaleContext';
import { useMetrics } from '../contexts/MetricsContext';
import type { SportId } from '../types';
export function SportMetrics() {
  const {
    sport: param
  } = useParams();
  const {
    t,
    tl
  } = useLocale();
  const navigate = useNavigate();
  const metrics = useMetrics();
  const sport = (sports.includes(param as SportId) ? param : 'running') as SportId;
  const list = standaloneMetrics(sport);
  const groups = metricGroupsForSport(sport);
  return <div className="px-2 pb-2" data-id="element-4064" data-mp="2gfdpox0to8h6">
      <PageHeader title={t(`category.${sport}`)} subtitle={t('metrics.sports.subtitle')} showBack data-id="element-4065" data-mp="2gihzc9x23wf8" />

      <ul className="bee-rail -mx-2 flex gap-1 overflow-x-auto px-2 pb-1" role="tablist" data-id="element-4066" data-mp="27fguecjxgbd5">
        {sports.map(item => <li key={item} className="shrink-0" data-id="element-4067" data-mp="22oqrd5yz4klm">
            <FilterChip label={t(`category.${item}`)} active={sport === item} onClick={() => navigate(`/perfil/esportes/${item}`)} data-id="element-4068" data-mp="22mxj5m8ppsbi" />
          </li>)}
      </ul>

      <Card className="mt-2 p-2" data-id="element-4069" data-mp="1oozg3czudibb">
        <SectionTitle data-id="element-4070" data-mp="1oo7jbyuidejg">{t('metrics.units')}</SectionTitle>
        <ul className="flex gap-1" data-id="element-4071" data-mp="1jq5zhbp9db6x">
          {(['metric', 'imperial'] as const).map(system => <li key={system} data-id="element-4072" data-mp="1a2k0ch01kg26">
              <FilterChip label={t(`metrics.units.${system}`)} active={metrics.unitSystem === system} onClick={() => metrics.setUnitSystem(system)} data-id="element-4073" data-mp="1a1h7i3j5s3jm" />
            </li>)}
        </ul>
      </Card>

      <Card className="mt-2 px-2" data-id="element-4074" data-mp="153frjrh14kcd">
        <ul data-id="element-4075" data-mp="154ifbqj226o2">
          {list.map(metric => {
          const source = metric.derived ? findSportMetric(metric.derived.from) : undefined;
          const hint = source ? t('sport.metric.derivedHint', {
            source: tl(source.label)
          }) : metric.value === 0 ? t(metric.optional ? 'sport.metric.optionalEmpty' : 'metrics.noRecord') : undefined;
          return <MetricRow key={metric.id} label={tl(metric.label)} kind={metric.kind} value={metric.value} derived={Boolean(metric.derived)} hint={hint} onOpen={metric.chart && !metric.derived && metric.value > 0 ? () => navigate(`/perfil/grafico/${encodeURIComponent(metric.id)}`) : undefined} data-id="element-4076" data-mp="1o0gw5b6o8ifs" />;
        })}
          {groups.map(group => <SportMetricGroupRow key={group.id} group={group} data-id="element-4077" data-mp="9u3q3i4lzfop" />)}
        </ul>
      </Card>

      <SportProfileFields sport={sport} data-id="element-4078" data-mp="3i9doog9ai2rq" />

      <SportEvolutionChart key={sport} sport={sport} data-id="element-4079" data-mp="3iafbfhzo4dpa" />

      {sport === 'strength' && <section className="mt-3" data-id="element-4080" data-mp="3dcnc42wp1pb2">
          <SectionTitle data-id="element-4081" data-mp="3rau0msfr6cy2">{t('metrics.milestones')}</SectionTitle>
          <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-4082" data-mp="3rc52untjvcn2">
            {strengthMilestones.map(milestone => <li key={milestone.id} className="flex items-center gap-2 border-b border-sand-line py-2 last:border-b-0" data-id="element-4083" data-mp="16kmjp25phipj">
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-pill ${milestone.reached ? 'bg-gold text-deep' : 'bg-sand-deep text-muted'}`} data-id="element-4084" data-mp="11l9rs2zz9lg3">
                  {milestone.reached ? <CheckIcon size={15} strokeWidth={3} data-id="element-4085" data-mp="2bsz3pot8ekwt" /> : <CircleDashedIcon size={15} data-id="element-4086" data-mp="2bqw54ge0jjgh" />}
                </span>
                <span className="min-w-0 flex-1" data-id="element-4087" data-mp="2fshmof1hslx0">
                  <span className="block text-[13px] font-medium text-deep" data-id="element-4088" data-mp="2ksvkz5dakhhu">
                    {tl(milestone.label)}
                  </span>
                  <span className="bee-num mt-[2px] block text-[11px] text-muted" data-id="element-4089" data-mp="2pl5fkypcq5qg">
                    {tl(milestone.detail)}
                  </span>
                </span>
              </li>)}
          </ul>
        </section>}

      <p className="mt-2 px-1 text-[11px] leading-snug text-muted" data-id="element-4090" data-mp="33jn0yprsb657">
        {t('metrics.sports.note')}
      </p>
    </div>;
}